import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, Edit, BookOpen, Save, X, Link as LinkIcon, Tag, Loader2, ExternalLink } from 'lucide-react';
import { API } from '../../lib/apiBase';

const empty = {
  title: '',
  content: '',
  tags: '',
  source_url: '',
  category: 'general',
  brand: '',
  model: '',
};

const CATEGORY_OPTIONS = [
  { value: 'general', label: 'Genel' },
  { value: 'price_0km', label: '0km Fiyat Referansı' },
  { value: 'problem_review', label: 'Sorun & Kullanıcı Yorumu' },
];

const CATEGORY_LABELS = CATEGORY_OPTIONS.reduce((acc, o) => ({ ...acc, [o.value]: o.label }), {});

const CATEGORY_BADGE_CLASS = {
  general: 'bg-secondary text-foreground border-border',
  price_0km: 'bg-primary/10 text-primary border-primary/30',
  problem_review: 'bg-destructive/10 text-destructive border-destructive/30',
};

const KnowledgeBaseManager = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [filter, setFilter] = useState('all');

  const loadNotes = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/knowledge`, { withCredentials: true });
      setNotes(data);
    } catch (error) {
      console.error('Error loading notes:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const resetForm = () => {
    setForm(empty);
    setEditingId(null);
  };

  const handleScrape = async () => {
    if (!form.source_url) {
      alert('Önce bir URL girin');
      return;
    }
    setScraping(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/knowledge/scrape`,
        { url: form.source_url },
        { withCredentials: true }
      );
      setForm((prev) => ({
        ...prev,
        title: prev.title || data.title || '',
        content: data.content || prev.content,
      }));
    } catch (error) {
      alert(`Hata: ${error.response?.data?.detail || 'Sayfa çekilemedi'}`);
    } finally {
      setScraping(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        title: form.title,
        content: form.content,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        source_url: form.source_url,
        category: form.category,
        brand: form.brand,
        model: form.model,
      };
      if (editingId) {
        await axios.put(`${API}/admin/knowledge/${editingId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/knowledge`, payload, { withCredentials: true });
      }
      resetForm();
      loadNotes();
    } catch (error) {
      alert(`Hata: ${error.response?.data?.detail || 'Bir hata oluştu'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (note) => {
    setForm({
      title: note.title,
      content: note.content,
      tags: (note.tags || []).join(', '),
      source_url: note.source_url || '',
      category: note.category || 'general',
      brand: note.brand || '',
      model: note.model || '',
    });
    setEditingId(note.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu notu silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/knowledge/${id}`, { withCredentials: true });
      if (editingId === id) resetForm();
      loadNotes();
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const filteredNotes = notes.filter((n) => filter === 'all' || (n.category || 'general') === filter);

  return (
    <div data-testid="knowledge-base-manager">
      {/* Info Banner */}
      <div className="border border-border bg-secondary/30 p-4 mb-6 flex items-start gap-3">
        <BookOpen className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold mb-1">Bilgi Bankası Nasıl Çalışır?</p>
          <p className="text-muted-foreground leading-relaxed">
            Buraya eklediğiniz notlar AI yanıt verirken otomatik referans alınır. Bir <strong>kaynak URL</strong> verirseniz
            "Linkten Çek" ile sayfa içeriği otomatik dolar. <strong>0km Fiyat</strong> ve
            <strong> Sorun &amp; Kullanıcı Yorumu</strong> kategorileri ile marka/model bilgisi, AI'ın doğru kaynağı seçmesini güçlendirir.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Form */}
        <div className="border border-border bg-card p-6" data-testid="kb-form-section">
          <h2 className="text-xl font-heading font-bold mb-6">
            {editingId ? 'Notu Düzenle' : 'Yeni Bilgi Notu Ekle'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Category */}
            <div>
              <Label>Kategori *</Label>
              <Select value={form.category} onValueChange={(value) => setForm({ ...form, category: value })}>
                <SelectTrigger className="rounded-sm" data-testid="kb-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Source URL */}
            <div>
              <Label htmlFor="source_url">Kaynak URL (opsiyonel)</Label>
              <div className="flex gap-2">
                <Input
                  id="source_url"
                  type="url"
                  value={form.source_url}
                  onChange={(e) => setForm({ ...form, source_url: e.target.value })}
                  className="rounded-sm font-mono text-sm flex-1"
                  placeholder="https://..."
                  data-testid="kb-source-url-input"
                />
                <Button
                  type="button"
                  onClick={handleScrape}
                  disabled={scraping || !form.source_url}
                  variant="outline"
                  className="rounded-sm whitespace-nowrap"
                  data-testid="kb-scrape-button"
                >
                  {scraping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LinkIcon className="w-4 h-4 mr-2" />}
                  {scraping ? 'Çekiliyor...' : 'Linkten Çek'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                "Linkten Çek" tıklarsa sayfanın başlığı + metni otomatik doldurulur.
              </p>
            </div>

            {/* Brand / Model */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="kb-brand">Marka (opsiyonel)</Label>
                <Input
                  id="kb-brand"
                  value={form.brand}
                  onChange={(e) => setForm({ ...form, brand: e.target.value })}
                  className="rounded-sm"
                  placeholder="BMW"
                  data-testid="kb-brand-input"
                />
              </div>
              <div>
                <Label htmlFor="kb-model">Model (opsiyonel)</Label>
                <Input
                  id="kb-model"
                  value={form.model}
                  onChange={(e) => setForm({ ...form, model: e.target.value })}
                  className="rounded-sm"
                  placeholder="3 Serisi"
                  data-testid="kb-model-input"
                />
              </div>
            </div>

            {/* Title */}
            <div>
              <Label htmlFor="title">Başlık *</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                className="rounded-sm"
                placeholder="Örn: BMW 3 Serisi B47 zincir gergisi sorunu"
                data-testid="kb-title-input"
              />
            </div>

            {/* Content */}
            <div>
              <Label htmlFor="content">İçerik *</Label>
              <Textarea
                id="content"
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                required
                rows={8}
                className="rounded-sm font-body"
                placeholder="Detaylı bilgiyi yazın. Örnek: 'BMW 3 Serisi 2014-2019 B47 dizel motorlarda zincir gergisi 150bin km civarı arıza yapar. Belirti: motor çalışırken metalik tıkırtı.'"
                data-testid="kb-content-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Ne kadar detaylı yazarsanız AI o kadar iyi yanıt verir. Fiyat, km, model yılı, çözüm bilgilerini ekleyin.
              </p>
            </div>

            {/* Tags */}
            <div>
              <Label htmlFor="tags">Etiketler (virgülle ayır)</Label>
              <Input
                id="tags"
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                className="rounded-sm"
                placeholder="bmw, 3-serisi, b47, motor, zincir"
                data-testid="kb-tags-input"
              />
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={submitting} className="rounded-sm bg-primary" data-testid="kb-submit-button">
                {editingId ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                {submitting ? 'Kaydediliyor...' : editingId ? 'Notu Güncelle' : 'Not Ekle'}
              </Button>
              {editingId && (
                <Button type="button" variant="outline" onClick={resetForm} className="rounded-sm" data-testid="kb-cancel-button">
                  <X className="w-4 h-4 mr-2" />
                  İptal
                </Button>
              )}
            </div>
          </form>
        </div>

        {/* List */}
        <div className="border border-border bg-card p-6" data-testid="kb-list-section">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <h2 className="text-xl font-heading font-bold">Mevcut Notlar ({filteredNotes.length}/{notes.length})</h2>
            <div className="flex gap-1 flex-wrap" data-testid="kb-filter">
              <Button size="sm" variant={filter === 'all' ? 'default' : 'outline'} onClick={() => setFilter('all')} className="rounded-sm" data-testid="kb-filter-all">
                Tümü
              </Button>
              <Button size="sm" variant={filter === 'price_0km' ? 'default' : 'outline'} onClick={() => setFilter('price_0km')} className="rounded-sm" data-testid="kb-filter-price">
                0km Fiyat
              </Button>
              <Button size="sm" variant={filter === 'problem_review' ? 'default' : 'outline'} onClick={() => setFilter('problem_review')} className="rounded-sm" data-testid="kb-filter-problem">
                Sorun/Yorum
              </Button>
            </div>
          </div>

          {loading ? (
            <p className="text-muted-foreground">Yükleniyor...</p>
          ) : filteredNotes.length === 0 ? (
            <p className="text-muted-foreground">Bu filtrede not yok.</p>
          ) : (
            <div className="space-y-3 max-h-[700px] overflow-y-auto">
              {filteredNotes.map((note) => {
                const cat = note.category || 'general';
                return (
                  <div key={note.id} className="border border-border p-4" data-testid={`kb-item-${note.id}`}>
                    <div className="flex justify-between items-start gap-3 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                          <span className={`text-xs font-mono px-2 py-0.5 border ${CATEGORY_BADGE_CLASS[cat]}`} data-testid={`kb-category-badge-${note.id}`}>
                            {CATEGORY_LABELS[cat] || cat}
                          </span>
                          {(note.brand || note.model) && (
                            <span className="text-xs font-mono text-muted-foreground flex items-center gap-1">
                              <Tag className="w-3 h-3" />
                              {note.brand} {note.model}
                            </span>
                          )}
                        </div>
                        <h3 className="font-semibold">{note.title}</h3>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(note)} className="rounded-sm h-8 w-8 p-0" data-testid={`kb-edit-${note.id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(note.id)} className="rounded-sm h-8 w-8 p-0" data-testid={`kb-delete-${note.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-3 mb-2 whitespace-pre-wrap">
                      {note.content}
                    </p>
                    {note.source_url && (
                      <a
                        href={note.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline font-mono truncate max-w-full mb-2"
                        data-testid={`kb-source-link-${note.id}`}
                      >
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{note.source_url}</span>
                      </a>
                    )}
                    {note.tags && note.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {note.tags.map((tag) => (
                          <span key={tag} className="text-xs font-mono px-2 py-0.5 bg-secondary text-foreground border border-border">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBaseManager;
